import {
  DEFAULT_FORMAT_CONTEXT,
  absent,
  formatDate,
  formatDateTime,
  formatDecimal,
  formatMoney,
  formatPercent,
  formatPhone,
  type Absent,
  type FormatContext,
} from './format.js';

/**
 * The field types a workspace can build with (F-009, F-011).
 *
 * One list, declared once. Every renderer, every validator and every check
 * derives from it, so adding a type is a compile error everywhere it has to be
 * handled rather than a silent gap that renders blank.
 */
export const FIELD_TYPES = [
  'text',
  'textarea',
  'email',
  'phone',
  'url',
  'number',
  'decimal',
  'currency',
  'percent',
  'date',
  'datetime',
  'boolean',
  'picklist',
  'multipicklist',
  'lookup',
  'user',
  'file',
  'tags',
  'address',
  'formula',
  'rollup',
] as const;

export type FieldType = (typeof FIELD_TYPES)[number];

export function isFieldType(value: string): value is FieldType {
  return (FIELD_TYPES as readonly string[]).includes(value);
}

/**
 * Types whose value the workspace does not enter and cannot edit.
 *
 * Kept as a derived set rather than a boolean on each definition, so a new
 * derived type cannot arrive without deciding which side of this line it is
 * on - `check:fields` asserts every type appears in exactly one bucket.
 */
export const DERIVED_FIELD_TYPES: readonly FieldType[] = ['formula', 'rollup'];

export function isDerived(type: FieldType): boolean {
  return DERIVED_FIELD_TYPES.includes(type);
}

/**
 * Types Simple mode hides (UX-14).
 *
 * "Simple mode hides custom objects, formula fields, sharing rules and the
 * automation engine. One switch reveals them."
 */
export const ADVANCED_ONLY_FIELD_TYPES: readonly FieldType[] = ['formula', 'rollup'];

export interface PicklistOption {
  value: string;
  label: string;
  /** A mark colour token name, never a hex value. */
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info';
}

export interface FieldDefinition {
  key: string;
  label: string;
  type: FieldType;
  required: boolean;
  helpText?: string | null;
  /** For picklist and multipicklist. */
  options?: readonly PicklistOption[];
  /** For lookup: which object the value points at. */
  lookupObject?: string | null;
  /** For currency: the record's currency code, which is a field of its own. */
  currencyField?: string | null;
  /** For decimal, currency and percent. */
  scale?: number;
  isCustom: boolean;
  sortOrder: number;
}

/**
 * A field's value as it crosses the wire.
 *
 * Decimals are strings end to end (D-018). A lookup is an object rather than a
 * bare id, because a screen that has an id and no name has to go and ask for
 * one, and a UUID must never reach a person's eyes.
 */
export type LookupValue = { id: string; name: string; href?: string | null };
export type FileValue = { id: string; name: string; sizeBytes: number; contentType: string };
export type AddressValue = {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  region?: string | null;
  postcode?: string | null;
  country?: string | null;
};

export type FieldValue =
  | string
  | number
  | boolean
  | null
  // A plain array, not a readonly one: these values cross the wire, and the
  // published type has to be the type the client actually receives.
  | string[]
  | LookupValue
  | FileValue
  | AddressValue;

/**
 * What a field's value looks like once rendered.
 *
 * Either text to show, or a structured value a renderer draws itself (a
 * lookup's link, a picklist's chip), or an `Absent` saying which kind of
 * nothing this is. Never an empty string standing in for all three - "no
 * value", "not permitted to see" and "not applicable" are different answers
 * and a blank cell says none of them (P-3, P-5).
 */
export type RenderedValue =
  | { kind: 'text'; text: string }
  | { kind: 'absent'; absent: Absent }
  | { kind: 'lookup'; value: LookupValue }
  | { kind: 'file'; value: FileValue }
  | { kind: 'boolean'; value: boolean }
  | { kind: 'options'; values: readonly PicklistOption[] }
  | { kind: 'address'; value: AddressValue };

/**
 * The object shapes a field value can take, indexed for probing.
 *
 * Declared rather than cast through `unknown` (AR-21): these are the three
 * structured values the type already allows, and saying so lets the guards
 * below read properties without erasing what they know.
 */
type StructuredKey = keyof LookupValue | keyof FileValue | keyof AddressValue;

function asRecord(value: FieldValue): Partial<Record<StructuredKey, unknown>> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value : null;
}

function isLookup(value: FieldValue): value is LookupValue {
  const record = asRecord(value);
  return record !== null && typeof record['id'] === 'string' && typeof record['name'] === 'string';
}

function isFile(value: FieldValue): value is FileValue {
  const record = asRecord(value);
  return (
    record !== null &&
    typeof record['id'] === 'string' &&
    typeof record['name'] === 'string' &&
    typeof record['sizeBytes'] === 'number'
  );
}

function isAddress(value: FieldValue): value is AddressValue {
  // Any of the parts being a string is enough: an address with only a city is
  // an address, and demanding every part would render most of them as broken.
  const record = asRecord(value);
  const parts: Array<keyof AddressValue> = ['line1', 'line2', 'city', 'region', 'postcode', 'country'];
  return record !== null && parts.some((key) => typeof record[key] === 'string');
}

function isBlank(value: FieldValue): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * One field's value, made displayable.
 *
 * Every number, date and amount here goes through the one formatter. A
 * renderer that reached for `toLocaleString` would be caught by lint; a
 * renderer that concatenated its own currency string would not, so the
 * formatting happens here and the renderers only choose the shape.
 */
export function renderFieldValue(
  field: FieldDefinition,
  value: FieldValue,
  record: Readonly<Record<string, FieldValue>> = {},
  ctx: FormatContext = DEFAULT_FORMAT_CONTEXT,
): RenderedValue {
  if (isBlank(value)) return { kind: 'absent', absent: absent('unset', ctx) };

  switch (field.type) {
    case 'text':
    case 'textarea':
    case 'email':
    case 'url':
      return { kind: 'text', text: String(value) };

    case 'phone':
      return { kind: 'text', text: formatPhone(String(value), ctx) };

    case 'number':
      return { kind: 'text', text: formatDecimal(String(value), ctx, { maximumFractionDigits: 0 }) };

    case 'decimal':
      return {
        kind: 'text',
        text: formatDecimal(String(value), ctx, {
          minimumFractionDigits: field.scale ?? 2,
          maximumFractionDigits: field.scale ?? 2,
        }),
      };

    case 'currency': {
      // The record carries its own currency (F-023). A total rendered in the
      // workspace's currency when the record is in another one is a wrong
      // number that looks like a right one.
      const code = field.currencyField ? record[field.currencyField] : null;
      return {
        kind: 'text',
        text: formatMoney(String(value), ctx, typeof code === 'string' ? { currency: code } : {}),
      };
    }

    case 'percent':
      // A percent field stores a ratio, not money: 0.42 is forty-two per cent.
      // It is the one numeric field whose value is a number rather than a
      // decimal string, because nothing about it needs exactness past the
      // digits shown, and the one formatter still renders it.
      return {
        kind: 'text',
        text: formatPercent(Number(value), ctx, { decimals: field.scale ?? 0 }),
      };

    case 'date':
      return { kind: 'text', text: formatDate(String(value), ctx) };

    case 'datetime':
      return { kind: 'text', text: formatDateTime(String(value), ctx) };

    case 'boolean':
      return { kind: 'boolean', value: value === true };

    case 'picklist': {
      const option = (field.options ?? []).find((o) => o.value === value);
      // A stored value with no matching option is a real state - the option was
      // renamed or removed - and showing the raw value is more honest than
      // showing nothing, so long as it is not dressed up as a valid choice.
      return { kind: 'options', values: [option ?? { value: String(value), label: String(value) }] };
    }

    case 'multipicklist': {
      const chosen = Array.isArray(value) ? value : [];
      return {
        kind: 'options',
        values: chosen.map(
          (v) => (field.options ?? []).find((o) => o.value === v) ?? { value: v, label: v },
        ),
      };
    }

    case 'tags': {
      const chosen = Array.isArray(value) ? value : [];
      return { kind: 'options', values: chosen.map((v) => ({ value: v, label: v })) };
    }

    case 'lookup':
    case 'user':
      // Checked, not cast. A bare identifier arriving where a resolved lookup
      // belongs used to reach the renderer, which called `.split` on an
      // undefined name and took the entire screen down with it - a data
      // problem in one field expressed as a blank page. It renders as an
      // absence with its own reason now, and the raw identifier is not shown,
      // because a UUID must never reach a person's eyes.
      return isLookup(value)
        ? { kind: 'lookup', value }
        : { kind: 'absent', absent: absent('unresolved', ctx) };

    case 'file':
      return isFile(value)
        ? { kind: 'file', value }
        : { kind: 'absent', absent: absent('unresolved', ctx) };

    case 'address':
      return isAddress(value)
        ? { kind: 'address', value }
        : { kind: 'absent', absent: absent('unresolved', ctx) };

    case 'formula':
    case 'rollup':
      // A derived value arrives already computed and already typed by the
      // server; it is shown as text because the server decided its shape.
      return { kind: 'text', text: String(value) };
  }
}
